<template>
  <section
    class="album-changelog"
    role="region"
    aria-label="Album Changelog"
  >
    <div class="album-changelog__header">
      <h3 class="album-changelog__title">Changelog</h3>
      <span v-if="!isLoading && changelog.length > 0" class="album-changelog__count">
        {{ changelog.length }} {{ changelog.length === 1 ? 'change' : 'changes' }}
      </span>
    </div>

    <!-- Loading State -->
    <div
      v-if="isLoading && changelog.length === 0"
      class="album-changelog__loading"
      role="status"
      aria-label="Loading changelog entries"
    >
      <div
        v-for="i in 3"
        :key="`skeleton-${i}`"
        class="album-changelog__skeleton"
      ></div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="album-changelog__error">
      <div class="album-changelog__error-icon" aria-hidden="true">⚠️</div>
      <p class="album-changelog__error-message">{{ error }}</p>
      <button
        @click="handleRetry"
        class="album-changelog__retry-button"
        type="button"
      >
        Retry
      </button>
    </div>

    <!-- Empty State -->
    <div v-else-if="changelog.length === 0" class="album-changelog__empty">
      <div class="album-changelog__empty-icon" aria-hidden="true">📜</div>
      <p class="album-changelog__empty-message">No changes yet</p>
      <p class="album-changelog__empty-subtitle">This album hasn't been modified</p>
    </div>

    <!-- Changelog Timeline -->
    <div v-else class="album-changelog__timeline">
      <article
        v-for="entry in changelog"
        :key="entry.id"
        :class="[
          'album-changelog__entry',
          `album-changelog__entry--${getChangeTypeClass(entry.change_type)}`
        ]"
      >
        <time
          :datetime="entry.created_at"
          class="album-changelog__date"
        >
          {{ formatDate(entry.created_at) }}
        </time>

        <div class="album-changelog__change">
          <span
            class="album-changelog__icon"
            :aria-label="getChangeTypeLabel(entry.change_type)"
            aria-hidden="true"
          >
            {{ getChangeTypeIcon(entry.change_type) }}
          </span>
          <span class="album-changelog__change-text">
            {{ formatChangeText(entry) }}
          </span>
        </div>

        <p class="album-changelog__changed-by">
          Changed by: {{ entry.changed_by || entry.reviewer || 'Unknown' }}
        </p>
      </article>

      <!-- Load More Button (Pagination) -->
      <button
        v-if="pagination.hasMore"
        @click="handleLoadMore"
        :disabled="isLoading"
        class="album-changelog__load-more"
        type="button"
      >
        {{ isLoading ? 'Loading...' : 'Show More' }}
      </button>
    </div>
  </section>
</template>

<script>
import { onMounted, toRef } from 'vue'
import { useAlbumChangelog } from '@/composables/useAlbumChangelog'

export default {
  name: 'AlbumChangelog',
  props: {
    albumId: {
      type: [Number, String],
      required: true
    }
  },
  setup(props) {
    const albumIdRef = toRef(props, 'albumId')
    const {
      changelog,
      isLoading,
      error,
      pagination,
      fetchChangelog,
      retry,
      loadMore
    } = useAlbumChangelog(albumIdRef)

    // Fetch changelog when component mounts
    onMounted(() => {
      if (props.albumId) {
        fetchChangelog()
      }
    })

    /**
     * Get CSS class for change type
     */
    const getChangeTypeClass = (changeType) => {
      const typeMap = {
        'tier_change': 'tier-change',
        'Tier Change': 'tier-change',
        'rating_change': 'rating-change',
        'Individual Rating Change': 'rating-change',
        'Overall Rating Change': 'rating-change',
        'veto': 'veto',
        'Veto': 'veto',
        'album_added': 'album-added',
        'Album Added': 'album-added',
        'album_removed': 'album-removed',
        'Album Removed': 'album-removed'
      }
      return typeMap[changeType] || 'default'
    }

    /**
     * Get icon for change type
     */
    const getChangeTypeIcon = (changeType) => {
      const iconMap = {
        'tier_change': '🎯',
        'Tier Change': '🎯',
        'rating_change': '⭐',
        'Individual Rating Change': '⭐',
        'Overall Rating Change': '⭐',
        'veto': '⛔',
        'Veto': '⛔',
        'album_added': '➕',
        'Album Added': '➕',
        'album_removed': '➖',
        'Album Removed': '➖'
      }
      return iconMap[changeType] || '•'
    }

    /**
     * Get accessible label for change type
     */
    const getChangeTypeLabel = (changeType) => {
      const labelMap = {
        'tier_change': 'Tier changed',
        'Tier Change': 'Tier changed',
        'rating_change': 'Rating changed',
        'Individual Rating Change': 'Rating changed',
        'Overall Rating Change': 'Rating changed',
        'veto': 'Veto added',
        'Veto': 'Veto added',
        'album_added': 'Album added',
        'Album Added': 'Album added',
        'album_removed': 'Album removed',
        'Album Removed': 'Album removed'
      }
      return labelMap[changeType] || 'Change made'
    }

    /**
     * Format change text with old/new values
     */
    const formatChangeText = (entry) => {
      const {
        change_type,
        old_value,
        new_value,
        old_rating,
        new_rating,
        old_tier,
        new_tier,
        reviewer
      } = entry

      if (change_type === 'tier_change' || change_type === 'Tier Change') {
        const oldVal = old_tier || old_value || 'None'
        const newVal = new_tier || new_value || 'None'
        return `Tier changed: ${oldVal} → ${newVal}`
      }

      if (change_type === 'rating_change' || change_type.includes('Rating Change')) {
        const reviewerName = reviewer || 'Unknown'
        const oldVal = old_rating || old_value || '0'
        const newVal = new_rating || new_value || '0'
        return `Rating (${reviewerName}): ${oldVal} → ${newVal}`
      }

      if (change_type === 'veto' || change_type === 'Veto') {
        return 'Veto added'
      }

      if (change_type === 'album_added' || change_type === 'Album Added') {
        return 'Album added to session'
      }

      if (change_type === 'album_removed' || change_type === 'Album Removed') {
        return 'Album removed from session'
      }

      // Fallback
      const oldVal = old_rating || old_tier || old_value || 'N/A'
      const newVal = new_rating || new_tier || new_value || 'N/A'
      return `${change_type}: ${oldVal} → ${newVal}`
    }

    /**
     * Format date with relative time
     */
    const formatDate = (dateString) => {
      const now = new Date()
      const changeDate = new Date(dateString)
      const diffMs = now - changeDate
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'Just now'
      if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`

      // Fallback to absolute date
      return changeDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    /**
     * Handle retry button click
     */
    const handleRetry = () => {
      retry()
    }

    /**
     * Handle load more button click
     */
    const handleLoadMore = () => {
      loadMore()
    }

    return {
      changelog,
      isLoading,
      error,
      pagination,
      getChangeTypeClass,
      getChangeTypeIcon,
      getChangeTypeLabel,
      formatChangeText,
      formatDate,
      handleRetry,
      handleLoadMore
    }
  }
}
</script>

<style scoped>
.album-changelog {
  margin-top: 24px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.album-changelog__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 16px;
}

.album-changelog__title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #f3f3f3;
}

.album-changelog__count {
  font-size: 14px;
  color: #a0a0a0;
}

/* Loading State */
.album-changelog__loading {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.album-changelog__skeleton {
  height: 80px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Error State */
.album-changelog__error {
  text-align: center;
  padding: 32px;
}

.album-changelog__error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.album-changelog__error-message {
  color: #f87171;
  margin-bottom: 16px;
  font-size: 16px;
}

.album-changelog__retry-button {
  padding: 8px 16px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.9) 0%, rgba(37, 99, 235, 0.9) 100%);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.album-changelog__retry-button:hover {
  background: linear-gradient(135deg, rgba(37, 99, 235, 1) 0%, rgba(29, 78, 216, 1) 100%);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.album-changelog__retry-button:active {
  transform: translateY(1px);
}

/* Empty State */
.album-changelog__empty {
  text-align: center;
  padding: 32px;
}

.album-changelog__empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.album-changelog__empty-message {
  color: #a0a0a0;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
}

.album-changelog__empty-subtitle {
  color: #6b7280;
  font-size: 14px;
}

/* Timeline */
.album-changelog__timeline {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.album-changelog__entry {
  padding: 12px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  border-left: 4px solid var(--change-type-color, #6b7280);
  transition: transform 0.2s, box-shadow 0.2s;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.album-changelog__entry:hover {
  transform: translateX(2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  background: rgba(255, 255, 255, 0.12);
}

/* Change Type Colors */
.album-changelog__entry--tier-change {
  --change-type-color: #3b82f6; /* Blue */
}

.album-changelog__entry--rating-change {
  --change-type-color: #fbbf24; /* Yellow */
}

.album-changelog__entry--veto {
  --change-type-color: #ef4444; /* Red */
}

.album-changelog__entry--album-added {
  --change-type-color: #10b981; /* Green */
}

.album-changelog__entry--album-removed {
  --change-type-color: #6b7280; /* Gray */
}

.album-changelog__date {
  font-size: 14px;
  color: #a0a0a0;
  margin-bottom: 8px;
  display: block;
}

.album-changelog__change {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.album-changelog__icon {
  font-size: 20px;
  line-height: 1;
}

.album-changelog__change-text {
  font-size: 16px;
  font-weight: 500;
  color: #f3f3f3;
}

.album-changelog__changed-by {
  font-size: 14px;
  color: #a0a0a0;
  margin: 0;
}

/* Load More Button */
.album-changelog__load-more {
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.08);
  color: #f3f3f3;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
  align-self: center;
  width: 100%;
  max-width: 200px;
}

.album-changelog__load-more:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.12);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.album-changelog__load-more:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .album-changelog {
    padding: 12px;
    margin-top: 16px;
  }

  .album-changelog__title {
    font-size: 16px;
  }

  .album-changelog__count {
    font-size: 12px;
  }

  .album-changelog__entry {
    padding: 10px;
  }

  .album-changelog__icon {
    font-size: 18px;
  }

  .album-changelog__change-text {
    font-size: 14px;
  }

  .album-changelog__date,
  .album-changelog__changed-by {
    font-size: 12px;
  }

  .album-changelog__empty,
  .album-changelog__error {
    padding: 24px 16px;
  }

  .album-changelog__empty-icon,
  .album-changelog__error-icon {
    font-size: 40px;
  }
}
</style>
